import { GetUserProjectsForTaskList } from '../../../../../services/api/project-api';
import { GetTaskTypesSortedByName, GetDefaultTaskTypeForTask } from '../../../../../services/api/task-types-api';
import * as yup from 'yup';
import { createDynamicInputChangeHandler } from '../../../../../components/DynamicField/Tasks/utils/fieldsUtils';
import TaskDynamicFieldsForm from '../../../../../components/DynamicField/Tasks/Form/TaskDynamicFieldsForm';
import { businessAdd } from '../../../../../util/business-days';
const isSTR = window["APP_SETTINGS"].CONFIGURED_DASHBOARD;

// TAD-5644 — was used to configure moment-business-days's working weekdays
// (Mon-Fri). Now a no-op because the replacement util in
// `src/util/business-days.js` hardcodes Mon-Fri. Kept as an exported no-op so
// existing call sites in task-create.js / task-edit.jsx don't need to change
// in this PR; the calls (and this export) can be removed in a follow-up.
export const SetBusinessDays = () => {};

export const GetBlankWorkItem = (currentUserId) => {
    return {
        Name: "",
        Due: new Date(),
        Start: new Date(),
        ProjectId: 0,
        assignedBy: currentUserId,
        PriorityId: 0,
        description: "",
        StatusId: 0,
        workItemType: 0,
        isArchived: false,
        endDateChange: false,
        startDateChange: false,
    };
};

export const GetActiveProjectsForTask = async (setActiveProjects) => {
    let result = await GetUserProjectsForTaskList();
    if (result.status === 200) {
        setActiveProjects(result.data);
        return result.data;
    }
    return result;
}

export const GetTaskTypes = async (setWorkItemTypes) => {
    let result = await GetTaskTypesSortedByName();
    if (result.status === 200) {
        setWorkItemTypes(result.data);
    }
    return result;
}

export const GetDefaultTaskType = async () => {
    let result = await GetDefaultTaskTypeForTask();
    if (result.status === 200) {
        return result.data?.Id ?? 0;
    }
    return 0;
}

export const isUseCase3 = (selectedMembersApprove, selectedMembersEdit, setSelectedMembersEdit, selectedMembersView, selectedMembersOwner, suppressBaseWorkflow = false) => {
    // TAD-5798: when Sync Member Permissions drives membership for this task type (Base Workflow
    // suppressed), do NOT force the lone owner/creator into the Editor list and skip the
    // use-case-3 routing — the Sync automation assigns Member Permissions on the initial transition,
    // and the backend suppresses the matching owner→editor normalization. Defaulted to false so all
    // existing behavior is preserved when suppression is off.
    if (suppressBaseWorkflow) {
        return false;
    }
    if (!selectedMembersOwner || selectedMembersOwner.length !== 1) {
        return false;
    }
    //if there are more than 1 owners then we return false above, so safe to assume following
    const ownerId = selectedMembersOwner[0];
    let ownerIsEditor = false;
    let memberApproverCount = selectedMembersApprove.length;
    let memberIsEditor = false;

    let MembersEdit = selectedMembersEdit;
    if (selectedMembersEdit.length === 0 && !(selectedMembersEdit.includes(ownerId) || selectedMembersApprove.includes(ownerId) || selectedMembersView.includes(ownerId))) {
        MembersEdit.push(ownerId);
        setSelectedMembersEdit(MembersEdit);
    }

    if (selectedMembersEdit.length === 1 && selectedMembersEdit.includes(ownerId))
        ownerIsEditor = true;
    else if (selectedMembersEdit.length === 1 && !selectedMembersEdit.includes(ownerId))
        memberIsEditor = true;

    return (ownerIsEditor && memberApproverCount > 0 && !memberIsEditor);
}

export const validationSchema = (labels) => {
    return yup.object({
        ProjectId: yup.number().integer().moreThan(0, `Select A Valid ${labels?.ProjectSingular ?? "Project"}`).required(),
        Name: yup.string().required(`${labels?.TaskSingular ?? "Task"} Name Required`).matches(/^\s*\S.*$/, '* This field cannot contain only blankspaces'),
        workItemType: yup.number().integer().moreThan(0, `Select A Valid ${labels?.TaskSingular ?? "Task"} Management Type`).required(),
        PriorityId: yup.number().integer().moreThan(0, "Select A Valid Priority").lessThan(5, "Select A Valid Priority").required(),
        Start: yup.date().typeError("Enter A Valid Date").max(yup.ref('Due'), "Start Date Cannot Be After End Date").required("Please Enter Valid Start Date").nullable(),
        Due: yup.date().typeError("Not a Date").min(yup.ref('Start'), "End Date Cannot Be Before Start Date").required("Please Enter Valid Due Date").nullable(),
        allApprovers: yup.boolean(),
    })
}

// Computes the due date based on the default task type's duration.
// Mirrors the logic of setEndDate() in task-details-section.js so the
// default-task-type → due-date sync also applies on initial render, not only
// when the user changes the task type dropdown.
const computeDueDateForTaskType = (taskType) => {
    const today = new Date();
    if (!taskType || !taskType.Days) return today;
    if (taskType.IsBusinessDays) {
        return businessAdd(new Date(), taskType.Days);
    }
    const due = new Date(today);
    due.setDate(due.getDate() + taskType.Days);
    return due;
};

export const initialValues = (projectId, defaultTaskType, workItemTypes = []) => {
    const currTaskType = workItemTypes.find(t => t.Id === defaultTaskType);
    return {
        ProjectId: projectId,
        Name: "",
        workItemType: defaultTaskType,
        PriorityId: 0,
        Start: new Date(),
        Due: computeDueDateForTaskType(currTaskType),
        allApprovers: false,
        endDateChange: false,
        selectedProject: [],
    }
}

export const renderTaskDetailDynamicFields = ({ dynamicFields, setDynamicFields, disableErrors, taskWithWorkflow, key = null, disabled = false, disabledAddDelete, isForm = false, taskId }) => {
    if (!dynamicFields || dynamicFields.length <= 0) return null;

    return dynamicFields.map((field) => {
        return (
            <TaskDynamicFieldsForm
                key={key ? key : `${field.Id}_${field.FieldType}`}
                field={field}
                handleChange={createDynamicInputChangeHandler({
                    field,
                    setDynamicFields,
                    isForm,
                })}
                setDynamicFields={setDynamicFields}
                disableErrors={disableErrors}
                disabled={disabled || (isSTR === "STR" ? (!isForm && field.IsDisabled) : field.IsDisabled)}
                taskWithWorkflow={taskWithWorkflow}
                disabledAddDelete={disabledAddDelete}
                isForm={isForm}
                taskId={taskId}
            />
        );
    });
}

// Annotates a project's Members/Groups with IsProjectMember so the assignment
// panels can tell project-level membership apart from task-level membership.
//
// The nested `member.User` / `group.Group` can be null: the API leaves them null
// for an orphaned association — a member whose user account, or a group, was
// soft-deleted (IsDeleted = 1) but never unlinked from the project. Writing
// IsProjectMember onto that null object is what crashes the task create/edit page
// with "Cannot set properties of null (setting 'IsProjectMember')". The guards
// below skip the orphaned rows instead of throwing. (The unguarded loops shipped
// with the membership panels in PR #1382 / TAD-4842.)
//
// Mutates `project` in place to match the existing call sites; also returns it so
// the behaviour is easy to assert in tests.
export const markProjectMembership = (project) => {
    if (!project) return project;

    (project.Members ?? []).forEach((member) => {
        if (!member) return;
        member.IsProjectMember = true;
        if (member.User) {
            member.User.IsProjectMember = true;
        }
    });

    (project.Groups ?? []).forEach((group) => {
        if (!group) return;
        group.IsProjectMember = true;
        if (group.Group) {
            group.Group.IsProjectMember = true;
        }
    });

    return project;
};

export const checkIfUserIsOwner = (result, userID, isMultiOwner) => {
    if (!result) return false;

    if (!isMultiOwner) {
        return result?.CreatedById === userID;
    }

    const isOwnerInMembers = result?.Members?.some(
        (member) => member.UserId === userID && member.IsOwner
    );

    if (isOwnerInMembers) return true;

    const isOwnerInGroups = result?.Groups?.some((group) => {
        if (!group.IsOwner) return false;
        return group.GroupMembers?.some((member) => member.UserId === userID);
    });

    return isOwnerInGroups;
};
